import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

import {
    Alert,
    Button,
    CircularProgress,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogContent,
    DialogActions,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    OutlinedInput,
} from '@mui/material';

import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import SearchIcon from '@mui/icons-material/Search';

import Sidebar from '../../Components/Sidebar/Sidebar';
import Navbar from '../../Components/Navbar/Navbar';
import DateInput from '../../Components/DateInput/DateInput';
import CuatomerReceipt from '../../Components/CuatomerReceipt/CuatomerReceipt';

import { ordersAPI, settingsAPI, categoryAPI, productAPI } from '../../services/apiService';

import './OrdersReport.css';

const persianDigits = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹'];

const toPersianNumber = (value, language) => {
    if (value === null || value === undefined) {
        return '-';
    }
    const number = Number(value);
    if (!Number.isFinite(number)) {
        return '-';
    }
    const formatted = number.toLocaleString('en-US');
    if (language !== 'fa') {
        return formatted;
    }
    return formatted.replace(/\d/g, (digit) => persianDigits[digit]);
};

function OrdersReport() {
    const { t, i18n } = useTranslation();
    const [reportType, setReportType] = useState('single');
    const [selectedDate, setSelectedDate] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const reportRef = useRef(null);
    const locale = i18n.language === 'fa' ? 'fa-IR' : 'en-US';
    const isPersian = i18n.language === 'fa';
    const direction = isPersian ? 'rtl' : 'ltr';
    const currency = isPersian ? 'تومان' : 'Toman';
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [openReceiptDialog, setOpenReceiptDialog] = useState(false);
    const [taxPercent, setTaxPercent] = useState(0);
    const [brandName, setBrandName] = useState('');
    const [logo, setLogo] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [addres, setAddres] = useState('');
    const customerReceiptRef = useRef(null);
    const [categories, setCategories] = useState([]);
    const [products, setProducts] = useState([]);
    const [showItemFilters, setShowItemFilters] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedItemIds, setSelectedItemIds] = useState([]);
    const [filtersLoading, setFiltersLoading] = useState(true);
    useEffect(() => {
        let cancelled = false;

        const loadFilterData = async () => {
            try {
                setFiltersLoading(true);

                const [categoryResult, productResult] = await Promise.all([
                    categoryAPI.getAll({
                        force: true,
                        fallback: [],
                    }),
                    productAPI.getAll({
                        force: true,
                        fallback: [],
                    }),
                ]);

                if (cancelled) return;

                const categoryList = Array.isArray(categoryResult)
                    ? categoryResult
                    : categoryResult?.results || categoryResult?.data || [];

                const productList = Array.isArray(productResult)
                    ? productResult
                    : productResult?.results || productResult?.data || [];

                setCategories(Array.isArray(categoryList) ? categoryList : []);
                setProducts(Array.isArray(productList) ? productList : []);
            } catch (error) {
                console.error('Failed to load report filters:', error);

                if (!cancelled) {
                    setSnackbar({
                        open: true,
                        message: error?.message || 'Failed to load categories and products',
                        severity: 'error',
                    });
                }
            } finally {
                if (!cancelled) {
                    setFiltersLoading(false);
                }
            }
        };

        loadFilterData();

        return () => {
            cancelled = true;
        };
    }, []);

    const filteredProducts = useMemo(() => {
        if (!selectedCategory) {
            return [];
        }

        return products.filter((product) => String(product.item_type) === String(selectedCategory));
    }, [products, selectedCategory]);

    const handleCategoryChange = (event) => {
        setSelectedCategory(event.target.value);
        setSelectedItemIds([]);
        setOrders([]);
    };

    const handleItemsChange = (event) => {
        const { value } = event.target;
        setSelectedItemIds(typeof value === 'string' ? value.split(',') : value);
        setOrders([]);
    };

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const settingsData = await settingsAPI.getSettings();
                setTaxPercent(Number(settingsData?.tax || 0));
                setBrandName(settingsData?.brand_name || '');
                setLogo(settingsData?.logo || '');
                setPhoneNumber(settingsData?.phone_number || '');
                setAddres(settingsData?.address || '');
            } catch (error) {
                console.error('Failed to load restaurant settings:', error);
            }
        };
        fetchSettings();
    }, []);

    const getReportDates = () => {
        if (reportType === 'single') {
            if (!selectedDate) {
                return null;
            }

            return {
                created_after: selectedDate,
                created_before: selectedDate,
            };
        }

        if (!fromDate || !toDate) {
            return null;
        }

        if (fromDate > toDate) {
            return null;
        }

        return {
            created_after: fromDate,
            created_before: toDate,
        };
    };

    const handleSearch = async () => {
        const reportDates = getReportDates();

        if (!reportDates) {
            setSnackbar({
                open: true,
                message:
                    reportType === 'single'
                        ? t('orders_report.select_date_first')
                        : t('orders_report.select_valid_date_range'),
                severity: 'warning',
            });

            return;
        }

        if (reportType === 'range' && fromDate > toDate) {
            setSnackbar({
                open: true,
                message: t('orders_report.invalid_date_range'),
                severity: 'warning',
            });

            return;
        }

        try {
            setLoading(true);
            if (selectedCategory && filteredProducts.length === 0) {
                setOrders([]);
                setSnackbar({
                    open: true,
                    message: 'This category has no items.',
                    severity: 'info',
                });
                return;
            }
            let itemIdsToSend = [];
            if (selectedCategory) {
                if (selectedItemIds.length > 0) {
                    itemIdsToSend = selectedItemIds;
                } else {
                    itemIdsToSend = filteredProducts.map((product) => String(product.id));
                }
            }
            const reportParams = {
                ...reportDates,
                ...(selectedCategory ? { item_id: itemIdsToSend } : {}),
            };
            const result = await ordersAPI.getReport(reportParams, {
                force: true,
            });
            setOrders(Array.isArray(result) ? result : []);
            setOrders(Array.isArray(result) ? result : []);
        } catch (error) {
            console.error(error);
            setSnackbar({
                open: true,
                message: error.message || t('orders_report.fetch_error'),
                severity: 'error',
            });
        } finally {
            setLoading(false);
        }
    };

    const totalIncome = useMemo(
        () => orders.reduce((sum, order) => sum + Number(order.total_price || 0), 0),
        [orders]
    );

    const reportDayCount = useMemo(() => {
        if (reportType === 'single') {
            return selectedDate ? 1 : 0;
        }

        if (!fromDate || !toDate || fromDate > toDate) {
            return 0;
        }

        const start = new Date(`${fromDate}T00:00:00`);
        const end = new Date(`${toDate}T00:00:00`);

        const difference = end.getTime() - start.getTime();

        return Math.floor(difference / (1000 * 60 * 60 * 24)) + 1;
    }, [reportType, selectedDate, fromDate, toDate]);

    const averageIncomePerDay = useMemo(() => {
        if (!reportDayCount) {
            return 0;
        }

        return totalIncome / reportDayCount;
    }, [totalIncome, reportDayCount]);

    const sortedOrders = useMemo(
        () => [...orders].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
        [orders]
    );

    const formatTime = (date) => {
        if (!date) return '-';

        return new Date(date).toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    };

    const formatDateTime = (date) => {
        if (!date) return '-';

        return new Intl.DateTimeFormat(locale, {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(new Date(date));
    };

    const formatNumber = (value) => new Intl.NumberFormat(locale).format(Number(value || 0));

    const getDailyOrderNumber = (currentOrder) => {
        if (!currentOrder || !orders.length) {
            return '-';
        }

        const orderDate = new Date(currentOrder.created_at).toDateString();
        const sameDayOrders = [...orders]
            .filter((item) => new Date(item.created_at).toDateString() === orderDate)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        const index = sameDayOrders.findIndex((item) => item.id === currentOrder.id);

        return index !== -1 ? index + 1 : '-';
    };

    const handleCloseSnackbar = (_, reason) => {
        if (reason === 'clickaway') {
            return;
        }

        setSnackbar((prev) => ({
            ...prev,
            open: false,
        }));
    };

    const getItemsTotalPrice = (order) => {
        if (!order?.items || !Array.isArray(order.items)) {
            return 0;
        }

        return order.items.reduce(
            (total, item) => total + Number(item.item_price || 0) * Number(item.quantity || 0),
            0
        );
    };

    const formatReportDate = (date) => {
        if (!date) return '-';

        const [year, month, day] = date.split('-').map(Number);
        const parsedDate = new Date(year, month - 1, day);

        if (Number.isNaN(parsedDate.getTime())) {
            return date;
        }

        return new Intl.DateTimeFormat(isPersian ? 'fa-IR-u-ca-persian' : 'en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        }).format(parsedDate);
    };

    const handleDownloadPDF = () => {
        if (!orders.length) {
            setSnackbar({
                open: true,
                message: t('orders_report.no_report_to_download'),
                severity: 'warning',
            });

            return;
        }

        if (!reportRef.current) {
            return;
        }

        const printWindow = window.open('', '_blank', 'width=1400,height=900');

        if (!printWindow) {
            setSnackbar({
                open: true,
                message: t('orders_report.popup_blocked'),
                severity: 'error',
            });

            return;
        }

        const printReportStyles = `
            @font-face {
                font-family: "Vazir";
                src: url("${window.location.origin}/fonts/Vazir.ttf")
                    format("truetype");
                font-weight: normal;
                font-style: normal;
            }

            * {
                box-sizing: border-box;
            }

            @page {
                size: A4 portrait;
                margin: 10mm;
            }

            html,
            body {
                margin: 0;
                padding: 0;
                width: 100%;
                background: white;
            }

            body {
                font-family: "Vazir", Arial, sans-serif;
                direction: rtl;
                color: #1f2937;

                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
            }

            .print-report {
                width: 100%;
                direction: rtl;
            }

            .print-header {
                margin-bottom: 20px;
                padding-bottom: 15px;
                border-bottom: 2px solid #365f4c;
            }

            .print-title {
                font-size: 24px;
                font-weight: bold;
                margin-bottom: 8px;
            }

            .print-date {
                font-size: 14px;
                color: #666;
            }

            .print-summary {
                display: flex;
                gap: 15px;
                margin-bottom: 20px;
            }

            .print-summary-card {
                border: 1px solid #d1d5db;
                padding: 12px 18px;
                border-radius: 8px;
                min-width: 120px;
                background: #f9fafb;
            }

            .print-summary-label {
                display: block;
                font-size: 12px;
                color: #666;
                margin-bottom: 5px;
            }

            .print-summary-value {
                display: block;
                font-size: 18px;
                font-weight: bold;
                color: #365f4c;
            }

            .print-table {
                width: 100%;
                border-collapse: collapse;
                table-layout: fixed;
                direction: rtl;
            }

            .print-table thead {
                display: table-header-group;
            }

            .print-table th {
                background: #365f4c !important;
                color: white !important;
                font-size: 10px;
                padding: 10px 6px;
                text-align: center;
                border: 1px solid #365f4c;
            }

            .print-table td {
                font-size: 8px;
                padding: 8px 6px;
                border: 1px solid #d1d5db;
                vertical-align: top;
                text-align: center;
                word-wrap: break-word;
                overflow-wrap: break-word;
            }

            .print-table tr {
                page-break-inside: avoid;
                break-inside: avoid;
            }

            .print-items-cell {
                text-align: right !important;
            }

            .print-item {
                display: flex;
                justify-content: space-between;
                gap: 10px;

                padding: 5px 0;

                border-bottom: 1px dashed #d1d5db;

                direction: rtl;
            }

            .print-item:last-child {
                border-bottom: none;
            }

            .print-item-name {
                flex: 1;
                text-align: right;
                font-weight: 500;
            }

            .print-item-quantity {
                white-space: nowrap;
                color: #555;
            }

            .print-footer {
                margin-top: 25px;
                padding-top: 12px;
                border-top: 1px solid #d1d5db;

                font-size: 11px;
                color: #666;
            }

            @media print {

                .print-report {
                    width: 100%;
                }

                .print-table {
                    page-break-inside: auto;
                }

                .print-table tr {
                    page-break-inside: avoid;
                    page-break-after: auto;
                }
            }
        `;

        const printContent = `
            <div class="print-report">
                ${reportRef.current.innerHTML}
            </div>
        `;

        printWindow.document.open();

        printWindow.document.write(`
            <!DOCTYPE html>
            <html lang="${isPersian ? 'fa' : 'en'}" dir="${direction}">
                <head>
                    <meta charset="UTF-8" />

                    <title>
                       ${t('orders_report.title')} ${formatReportDate(selectedDate)}
                    </title>

                    <style>
                        ${printReportStyles}
                    </style>
                </head>

                <body>
                    ${printContent}
                </body>
            </html>
        `);

        printWindow.document.close();

        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 300);
    };

    const handleDownloadExcel = () => {
        if (!orders.length) {
            setSnackbar({
                open: true,
                message: t('orders_report.no_report_to_download'),
                severity: 'warning',
            });

            return;
        }

        const excelData = sortedOrders.map((order) => ({
            [t('orders_report.factor_number')]: getDailyOrderNumber(order),

            [t('customer_name')]: order.customer_name || t('anonymous_customer'),

            [reportType === 'range' ? t('orders_report.date_and_time') : t('orders_report.time')]:
                reportType === 'range'
                    ? formatDateTime(order.created_at)
                    : formatTime(order.created_at),

            [t('orders_report.table')]: order?.table?.table_id || t('orders_report.takeaway'),

            [t('orders_report.items')]:
                Array.isArray(order.items) && order.items.length
                    ? order.items
                          .map(
                              (item) =>
                                  `${item.item_title || t('common.unknown')} × ${
                                      item.quantity || 1
                                  }`
                          )
                          .join('\n')
                    : '-',

            [t('total_price')]: getItemsTotalPrice(order),

            [t('orders_report.final_price')]: Number(order.total_price || 0),
        }));

        const worksheet = XLSX.utils.json_to_sheet(excelData);

        const workbook = XLSX.utils.book_new();

        XLSX.utils.book_append_sheet(
            workbook,
            worksheet,
            t('orders_report.excel_sheet_name') || 'Orders'
        );

        worksheet['!cols'] = [
            { wch: 14 },
            { wch: 25 },
            { wch: 22 },
            { wch: 14 },
            { wch: 45 },
            { wch: 18 },
            { wch: 18 },
        ];

        worksheet['!rows'] = sortedOrders.map(() => ({
            hpt: 30,
        }));

        const fileName =
            reportType === 'single'
                ? `orders-report-${selectedDate}.xlsx`
                : `orders-report-${fromDate}-to-${toDate}.xlsx`;

        XLSX.writeFile(workbook, fileName);
    };

    const handleOpenReceipt = (order) => {
        setSelectedOrder(order);
        setOpenReceiptDialog(true);
    };

    const handleCloseReceipt = () => {
        setOpenReceiptDialog(false);
        setSelectedOrder(null);
    };

    const getReportDateLabel = () => {
        if (reportType === 'single') {
            return formatReportDate(selectedDate);
        }

        if (!fromDate || !toDate) {
            return '-';
        }

        return `${formatReportDate(fromDate)} - ${formatReportDate(toDate)}`;
    };

    const handleReportTypeChange = (type) => {
        setReportType(type);
        setOrders([]);

        if (type === 'single') {
            setFromDate('');
            setToDate('');
        } else {
            setSelectedDate('');
        }
    };

    return (
        <div className="orders-report-page">
            <Sidebar />
            <div className="orders-report-main">
                <Navbar />
                <div className="orders-report-content">
                    <div className="orders-report-header">
                        <Button
                            variant="contained"
                            startIcon={<PictureAsPdfIcon />}
                            onClick={handleDownloadPDF}
                            disabled={!orders.length}
                            className="pdf-button"
                        >
                            {t('orders_report.print_pdf')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<TableViewIcon />}
                            onClick={handleDownloadExcel}
                            disabled={!orders.length}
                            className="excel-button"
                        >
                            {t('orders_report.export_excel')}
                        </Button>
                    </div>
                    <Paper className="report-filter-card">
                        <div className="report-filter">
                            <div className="filters-section">
                                <div className="date-filter-section">
                                    <div className="report-type-selector">
                                        <Button
                                            variant={
                                                reportType === 'single' ? 'contained' : 'outlined'
                                            }
                                            onClick={() => handleReportTypeChange('single')}
                                        >
                                            {t('orders_report.single_date')}
                                        </Button>
                                        <Button
                                            variant={
                                                reportType === 'range' ? 'contained' : 'outlined'
                                            }
                                            onClick={() => handleReportTypeChange('range')}
                                        >
                                            {t('orders_report.date_range')}
                                        </Button>
                                    </div>
                                    {reportType === 'single' ? (
                                        <DateInput
                                            label={t('orders_report.select_date')}
                                            value={selectedDate}
                                            onChange={setSelectedDate}
                                        />
                                    ) : (
                                        <>
                                            <DateInput
                                                label={t('orders_report.from_date')}
                                                value={fromDate}
                                                onChange={setFromDate}
                                            />
                                            <DateInput
                                                label={t('orders_report.to_date')}
                                                value={toDate}
                                                onChange={setToDate}
                                            />
                                        </>
                                    )}
                                </div>
                                <div className="advanced-filter-section">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showItemFilters}
                                                onChange={(event) => {
                                                    const { checked } = event.target;
                                                    setShowItemFilters(checked);
                                                    if (!checked) {
                                                        setSelectedCategory('');
                                                        setSelectedItemIds([]);
                                                        setOrders([]);
                                                    }
                                                }}
                                            />
                                        }
                                        label={
                                            t('orders_report.advanced_filters') ||
                                            'Filter by category and items'
                                        }
                                    />
                                    {showItemFilters && (
                                        <div className="item-category-filters">
                                            <FormControl sx={{ minWidth: 180 }} size="small">
                                                <InputLabel id="report-category-label">
                                                    {t('all_categories_label') || 'Category'}
                                                </InputLabel>
                                                <Select
                                                    labelId="report-category-label"
                                                    value={selectedCategory}
                                                    label={
                                                        t('orders_report.category') || 'Category'
                                                    }
                                                    onChange={handleCategoryChange}
                                                    disabled={filtersLoading || loading}
                                                >
                                                    <MenuItem value="">
                                                        {t('all_categories_label') ||
                                                            'All categories'}
                                                    </MenuItem>
                                                    {categories.map((category) => (
                                                        <MenuItem
                                                            key={category.id}
                                                            value={String(category.id)}
                                                        >
                                                            {category.name || category.title}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <FormControl
                                                sx={{ minWidth: 220, maxWidth: 360 }}
                                                size="small"
                                                disabled={
                                                    !selectedCategory || filtersLoading || loading
                                                }
                                            >
                                                <InputLabel id="report-items-label">
                                                    {t('orders_report.items') || 'Items'}
                                                </InputLabel>
                                                <Select
                                                    labelId="report-items-label"
                                                    multiple
                                                    value={selectedItemIds}
                                                    onChange={handleItemsChange}
                                                    input={
                                                        <OutlinedInput
                                                            label={
                                                                t('orders_report.items') || 'Items'
                                                            }
                                                        />
                                                    }
                                                    renderValue={(selected) => {
                                                        if (!selected.length) {
                                                            return (
                                                                t(
                                                                    'orders_report.all_category_items'
                                                                ) || 'All items in category'
                                                            );
                                                        }
                                                        return filteredProducts
                                                            .filter((product) =>
                                                                selected.includes(
                                                                    String(product.id)
                                                                )
                                                            )
                                                            .map(
                                                                (product) =>
                                                                    product.name || product.title
                                                            )
                                                            .join(', ');
                                                    }}
                                                >
                                                    {filteredProducts.map((product) => (
                                                        <MenuItem
                                                            key={product.id}
                                                            value={String(product.id)}
                                                        >
                                                            <Checkbox
                                                                checked={selectedItemIds.includes(
                                                                    String(product.id)
                                                                )}
                                                            />
                                                            <ListItemText
                                                                primary={
                                                                    product.name || product.title
                                                                }
                                                            />
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <Button
                                variant="contained"
                                startIcon={<SearchIcon />}
                                onClick={handleSearch}
                                disabled={loading || filtersLoading}
                                className="search-report-button"
                            >
                                {loading
                                    ? t('orders_report.loading')
                                    : t('orders_report.show_report')}
                            </Button>
                        </div>
                    </Paper>
                    <div className="report-summary">
                        <Paper className="summary-card">
                            <span>{t('orders_report.total_orders')}</span>

                            <strong>{toPersianNumber(orders.length, i18n.language)}</strong>
                        </Paper>

                        <Paper className="summary-card">
                            <span>{t('orders_report.total_income')}</span>

                            <strong>
                                {formatNumber(totalIncome)} {t('common.currency')}
                            </strong>
                        </Paper>

                        <Paper className="summary-card">
                            <span>{t('orders_report.average_income_per_day')}</span>
                            <strong>
                                {formatNumber(averageIncomePerDay)} {t('common.currency')}
                            </strong>
                        </Paper>
                    </div>
                    {loading && (
                        <div className="report-loading">
                            <CircularProgress />
                        </div>
                    )}
                    {!loading && (
                        <TableContainer component={Paper} className="orders-report-table">
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell align="center">
                                            {t('orders_report.factor_number')}
                                        </TableCell>

                                        <TableCell align="center">{t('customer_name')}</TableCell>

                                        <TableCell align="center">
                                            {reportType === 'range'
                                                ? t('orders_report.date_and_time')
                                                : t('orders_report.time')}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('orders_report.table')}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('orders_report.items')}
                                        </TableCell>

                                        <TableCell align="center">{t('total_price')}</TableCell>

                                        <TableCell align="center">
                                            {t('orders_report.final_price')}
                                        </TableCell>

                                        <TableCell align="center">
                                            {t('receipts') || 'Receipt'}
                                        </TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {orders.length === 0 ? (
                                        <TableRow>
                                            <TableCell
                                                colSpan={8}
                                                align="center"
                                                className="empty-report"
                                            >
                                                {selectedDate
                                                    ? t('orders_report.no_orders_found')
                                                    : t('orders_report.select_date')}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        sortedOrders.map((order, index) => (
                                            <TableRow key={order.id || index}>
                                                <TableCell align="center" data-label="#">
                                                    {formatNumber(getDailyOrderNumber(order))}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    data-label={t('orders_report.order_number')}
                                                    className="order-id-cell"
                                                >
                                                    {order.customer_name || t('anonymous_customer')}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    data-label={
                                                        reportType === 'range'
                                                            ? t('orders_report.date_and_time')
                                                            : t('orders_report.time')
                                                    }
                                                >
                                                    {reportType === 'range'
                                                        ? formatDateTime(order.created_at)
                                                        : formatTime(order.created_at)}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    data-label={t('orders_report.table')}
                                                >
                                                    {order?.table?.table_id ||
                                                        t('orders_report.takeaway')}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    data-label={t('orders_report.items')}
                                                    className="items-cell"
                                                >
                                                    {Array.isArray(order.items) &&
                                                    order.items.length ? (
                                                        <div className="order-items-list">
                                                            {order.items.map((item) => (
                                                                <div
                                                                    className="order-item-row"
                                                                    key={item.id}
                                                                >
                                                                    <span className="order-item-name">
                                                                        {item.item_title ||
                                                                            t('common.unknown')}
                                                                    </span>

                                                                    <span className="order-item-quantity">
                                                                        ×{' '}
                                                                        {toPersianNumber(
                                                                            item.quantity || 1,
                                                                            i18n.language
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        '-'
                                                    )}
                                                </TableCell>

                                                <TableCell
                                                    data-label={t('total_price')}
                                                    align="center"
                                                >
                                                    {formatNumber(getItemsTotalPrice(order))}
                                                </TableCell>

                                                <TableCell
                                                    align="center"
                                                    data-label={t('orders_report.final_price')}
                                                    className="price-cell"
                                                >
                                                    {formatNumber(order.total_price || 0)}

                                                    <small>{t('common.currency')}</small>
                                                </TableCell>

                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        onClick={() => handleOpenReceipt(order)}
                                                    >
                                                        {t('view_factors') || 'View Receipt'}
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                    <div ref={reportRef} className="print-report" style={{ display: 'none' }}>
                        <div className="print-header">
                            <div className="print-title">{t('orders_report.title')}</div>

                            <div className="print-date">{getReportDateLabel()}</div>
                        </div>

                        <div className="print-summary">
                            <div className="print-summary-card">
                                <span className="print-summary-label">
                                    {t('orders_report.total_orders')}
                                </span>

                                <strong className="print-summary-value">
                                    {formatNumber(orders.length)}
                                </strong>
                            </div>

                            <div className="print-summary-card">
                                <span className="print-summary-label">
                                    {t('orders_report.total_income')}
                                </span>

                                <strong className="print-summary-value">
                                    {formatNumber(totalIncome)} {t('common.currency')}
                                </strong>
                            </div>

                            <div className="print-summary-card">
                                <span className="print-summary-label">
                                    {t('orders_report.average_income_per_day')}
                                </span>
                                <strong className="print-summary-value">
                                    {formatNumber(averageIncomePerDay)}
                                </strong>
                            </div>
                        </div>

                        <table className="print-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '7%' }}>
                                        {t('orders_report.factor_number')}
                                    </th>

                                    <th style={{ width: '9%' }}>{t('customer_name')}</th>

                                    <th style={{ width: '9%' }}>
                                        {reportType === 'range'
                                            ? t('orders_report.date_and_time')
                                            : t('orders_report.time')}
                                    </th>

                                    <th style={{ width: '8%' }}>{t('orders_report.table')}</th>

                                    <th style={{ width: '40%' }}>{t('orders_report.items')}</th>

                                    <th style={{ width: '10%' }}>{t('total_price')}</th>

                                    <th style={{ width: '10%' }}>
                                        {t('orders_report.final_price')}
                                    </th>
                                </tr>
                            </thead>

                            <tbody>
                                {sortedOrders.map((order, index) => (
                                    <tr key={order.id || index}>
                                        <td>{formatNumber(getDailyOrderNumber(order))}</td>

                                        <td dir="ltr">
                                            {order.customer_name || t('anonymous_customer')}
                                        </td>

                                        <td>
                                            {reportType === 'range'
                                                ? formatDateTime(order.created_at)
                                                : formatTime(order.created_at)}
                                        </td>

                                        <td>{order?.table?.table_id || 'بیرون‌بر'}</td>

                                        <td className="print-items-cell">
                                            {Array.isArray(order.items) && order.items.length
                                                ? order.items.map((item) => (
                                                      <div className="print-item" key={item.id}>
                                                          <span className="print-item-name">
                                                              {item.item_title || 'نامشخص'}
                                                          </span>

                                                          <span className="print-item-quantity">
                                                              × {formatNumber(item.quantity || 1)}
                                                          </span>
                                                      </div>
                                                  ))
                                                : '-'}
                                        </td>

                                        <td>{formatNumber(getItemsTotalPrice(order))}</td>

                                        <td>{formatNumber(order.total_price || 0)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <div className="print-footer">{t('orders_report.generated_by')}</div>
                    </div>
                    <Dialog
                        open={openReceiptDialog}
                        onClose={handleCloseReceipt}
                        maxWidth="sm"
                        fullWidth
                    >
                        <DialogContent>
                            <CuatomerReceipt
                                ref={customerReceiptRef}
                                order={selectedOrder}
                                orders={orders}
                                brandName={brandName}
                                taxPercent={taxPercent}
                                logo={logo}
                                phoneNumber={phoneNumber}
                                addres={addres}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button
                                onClick={() => customerReceiptRef.current?.print()}
                                color="primary"
                            >
                                {t('print_customer_receipt') || 'Print Receipt'}
                            </Button>

                            <Button onClick={handleCloseReceipt}>{t('close') || 'Close'}</Button>
                        </DialogActions>
                    </Dialog>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={4000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                    >
                        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
                    </Snackbar>
                </div>
            </div>
        </div>
    );
}

export default OrdersReport;
